import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Skeleton from "react-loading-skeleton";
import { authService } from "../../../utils/authService";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { CiSquareChevUp, CiSquareChevDown } from "react-icons/ci";
import { useNavigate } from "react-router-dom";

interface Day {
  day: string;
  day_key: string;
  date: string;
  week: number;
  topics: string;
  videos: number;
  notes: number;
  notebooks?: number;
  mcq: number;
  coding: number;
}

interface Week {
  week: number;
  startDate: string;
  endDate: string;
  totalHours: number;
  days: Day[];
  topics: string | null;
}

interface CourseRoadmapData {
  course_roadmap: Week[];
}

interface CourseOverviewProps {
  onBack: () => void;
}

const CourseOverview: React.FC<CourseOverviewProps> = ({ onBack }) => {
  const navigate = useNavigate();
  
  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
      return;
    }
  }, [navigate]);

  const [data, setData] = useState<Week[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openWeeks, setOpenWeeks] = useState<Set<number>>(new Set());
  const [totalCodingChallenges, setTotalCodingChallenges] = useState(0);

  // Get filters from session storage
  const courseOverviewFilters = sessionStorage.getItem("courseOverviewFilters") || sessionStorage.getItem("dashboardFilters");
  let selectedCourse = "";
  let selectedBatch = "";
  let selectedSubject = "";

  if (courseOverviewFilters) {
    try {
      const filters = JSON.parse(courseOverviewFilters);
      selectedCourse = filters.course || "";
      selectedBatch = filters.batch || "";
      selectedSubject = filters.subject || "";
    } catch (error) {
      console.error("Error parsing course overview filters:", error);
    }
  }

  useEffect(() => {
    const fetchDataFromAPI = async () => {
      if (!selectedCourse || !selectedBatch || !selectedSubject) {
        setError("Please select course, batch, and subject from dashboard");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await authService.get(
          `course-roadmap/${selectedCourse}/${selectedBatch}/${selectedSubject}/`
        );
        const roadmapData: CourseRoadmapData = response.data;

        setData(roadmapData.course_roadmap);

        // Calculate total coding challenges
        const totalCoding = roadmapData.course_roadmap.reduce((total, week) => {
          return (
            total +
            week.days.reduce((weekTotal, day) => weekTotal + day.coding, 0)
          );
        }, 0);
        setTotalCodingChallenges(totalCoding);
      } catch (error: any) {
        console.error("Error fetching course roadmap data:", error);
        setError("Failed to load course roadmap data");
      } finally {
        setLoading(false);
      }
    };

    fetchDataFromAPI();
  }, [selectedCourse, selectedBatch, selectedSubject]);

  useEffect(() => {
    // Open the current week by date if it exists, otherwise open the first week
    if (data.length > 0) {
      const currentDate = new Date();
      const currentWeek = data.find((week) => {
        // Check if current date falls within the week's date range
        if (week.startDate && week.endDate) {
          const startDate = new Date(week.startDate);
          const endDate = new Date(week.endDate);
          return currentDate >= startDate && currentDate <= endDate;
        }
        return false;
      });

      if (currentWeek) {
        setOpenWeeks(new Set([currentWeek.week]));
      } else {
        // Fallback to first week if no current week found
        setOpenWeeks(new Set([data[0].week]));
      }
    }
  }, [data]);

  const toggleWeek = (weekNumber: number) => {
    const newOpenWeeks = new Set(openWeeks);
    if (newOpenWeeks.has(weekNumber)) {
      newOpenWeeks.delete(weekNumber);
    } else {
      newOpenWeeks.add(weekNumber);
    }
    setOpenWeeks(newOpenWeeks);
  };

  if (loading) {
    return (
      <div
        className="bg-white mt-2 border rounded-1 pt-2 p- px-2 me-2"
        style={{
          backgroundColor: "#F2EEEE",
          height: `calc(100vh - 58px)`,
          overflowY: "auto",
        }}
      >
        <div className="table-container p-1 mb-3 mt-4 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
        <div className="table-container p-1 mb-3 border rounded-1">
          <Skeleton height={35} style={{ backgroundColor: "#D7DCFF" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-white mt-2 border rounded-1 pt-2 p- px-2 me-2"
        style={{
          backgroundColor: "#F2EEEE",
          height: `calc(100vh - 58px)`,
          overflowY: "auto",
        }}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <p className="text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className=""
      style={{ backgroundColor: "#F2EEEE", height: `calc(100vh - 90px)` }}
    >
      <div className="p-0 my-0 me-2" style={{ backgroundColor: "#F2EEEE" }}>
        {/* Header with Back Button and Total Coding Challenges */}
        <div
          className="container-fluid bg-white mt-2 rounded-1 pt-2 mb-2"
          style={{
            border: "1px solid #ABABAB",
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <button
              className="btn btn-sm"
              onClick={onBack}
              style={{
                backgroundColor: "#E9EBFF",
                border: "none",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            >
              ← {selectedSubject}
            </button>
            <div className="text-end">
              <span className="fw-bold">
                Total Coding Challenges: {totalCodingChallenges}
              </span>
            </div>
          </div>
        </div>

        <div
          className="container-fluid bg-white mt-2 rounded-1 pt-2"
          style={{
            height: `calc(100vh - 65px)`,
            overflowY: "auto",
            border: "1px solid #ABABAB",
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
        >
          {data.map((week) => (
            <div
              className="mb-2 rounded p-1"
              key={week.week}
              style={{ border: "1px solid #ABABAB" }}
            >
              <div
                className="p-2 rounded-2 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: "#D7DCFF" }}
              >
                <div
                  className="d-flex p-0 justify-content-between flex-wrap"
                  style={{ cursor: "default" }}
                >
                  <h5 className="m-0 p-0 pe-3">Week {week.week}</h5>
                  {week.topics && (
                    <p className="m-0 p-0 text-sm" title={week.topics}>
                      {week.topics.length > 60
                        ? week.topics.substring(0, 60) + "..."
                        : week.topics}
                    </p>
                  )}
                </div>
                <div className="d-flex justify-content-between">
                  {openWeeks.has(week.week) && (
                    <>
                      <div className="d-flex align-items-center justify-content-start">
                        <h5 className="m-0 p-0 pe-2">{week.totalHours}hrs</h5>
                        <span
                          role="button"
                          title={`${week.totalHours} learning content has been assigned for week ${week.week}`}
                          style={{ fontSize: "20px", cursor: "default" }}
                          className="mb-1"
                        >
                          <IoMdInformationCircleOutline />
                        </span>
                      </div>
                    </>
                  )}
                  <h5 className="m-0 p-0 pe-5"></h5>

                  <button
                    className="btn btn-sm p-0 px-1 fw-bold"
                    onClick={() => toggleWeek(week.week)}
                    disabled={week.days && week.days.length === 0}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                    }}
                  >
                    {openWeeks.has(week.week) ? (
                      <CiSquareChevDown fontSize={25} className="ms-2" />
                    ) : (
                      <CiSquareChevUp fontSize={25} className="ms-2" />
                    )}
                  </button>
                </div>
              </div>
              {openWeeks.has(week.week) && week.days ? (
                <div className="">
                  {/* Headers Row */}
                  <div className="mb-2 d-flex justify-content-between align-items-center">
                    <div
                      className="border-end  border-white me-3"
                      style={{
                        padding: "10px",
                        cursor: "default",
                        width: "80px",
                      }}
                    ></div>
                    <div className="d-flex justify-content-between flex-grow-1 rounded-2 ps-3 ">
                      <div
                        className="d-flex align-items-center"
                        style={{ width: "100px", cursor: "default" }}
                      ></div>
                      <div
                        className="text-start d-flex align-items-center"
                        role="button"
                        style={{
                          fontSize: "14px",
                          cursor: "default",
                          width:
                            window.innerWidth >= 1280
                              ? "500px"
                              : window.innerWidth >= 1024
                              ? "300px"
                              : window.innerWidth >= 768
                              ? "200px"
                              : "120px",
                        }}
                      ></div>
                      <div
                        style={{
                          fontSize: "15px",
                          color: "#000",
                          width: "80px",
                          textAlign: "center",
                        }}
                      >
                        Videos
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          color: "#000",
                          width: "80px",
                          textAlign: "center",
                        }}
                      >
                        Notes
                      </div>
                      <div
                        title="Marimo notebooks"
                        style={{
                          fontSize: "15px",
                          color: "#000",
                          width: "90px",
                          textAlign: "center",
                        }}
                      >
                        Notebooks
                      </div>
                      <div
                        style={{
                          fontSize: "15px",
                          color: "#000",
                          width: "80px",
                          textAlign: "center",
                        }}
                      >
                        MCQ's
                      </div>
                      <div
                        title="Coding Challenges"
                        style={{
                          fontSize: "15px",
                          color: "#000",
                          width: "100px",
                          textAlign: "center",
                        }}
                      >
                        Coding C's
                      </div>
                    </div>
                  </div>

                  {/* Data Rows */}
                  {week.days.map((day) => {
                    const isCurrentDay =
                      day.date &&
                      (() => {
                        try {
                          const cleanDate = day.date
                            .replace(/(\d+)(st|nd|rd|th)/, "$1")
                            .replace(/(\w+)\s+(\d{2})$/, "$1 20$2");
                          const dayDate = new Date(cleanDate);
                          const today = new Date();
                          return (
                            dayDate.toDateString() === today.toDateString()
                          );
                        } catch {
                          return false;
                        }
                      })();

                    return (
                      <div
                        className="mb-2 d-flex justify-content-between align-items-center"
                        key={day.day_key}
                      >
                        <div
                          className="border-end border-1 border-black me-3"
                          style={{
                            padding: "10px",
                            cursor: "default",
                            width: "80px",
                          }}
                        >
                          Day {day.day}
                        </div>
                        <div
                          className="d-flex justify-content-between flex-grow-1 rounded-2 ps-3 p-1"
                          style={{
                            border: "1px solid #ABABAB",
                            backgroundColor: isCurrentDay
                              ? "#E9EBFF"
                              : "transparent",
                          }}
                        >
                          {day.date && (
                            <div
                              className="d-flex align-items-center"
                              style={{ width: "100px", cursor: "default" }}
                            >
                              {day.date}
                            </div>
                          )}
                          {day.topics && (
                            <div
                              className="text-start d-flex align-items-center"
                              role="button"
                              title={day.topics}
                              style={{
                                fontSize: "14px",
                                cursor: "default",
                                width:
                                  window.innerWidth >= 1280
                                    ? "500px"
                                    : window.innerWidth >= 1024
                                    ? "300px"
                                    : window.innerWidth >= 768
                                    ? "200px"
                                    : "120px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {day.topics.length > 60
                                ? day.topics.substring(0, 60) + "..."
                                : day.topics}
                            </div>
                          )}

                          <div
                            style={{
                              fontSize: "12px",
                              cursor: "default",
                              width: "80px",
                              textAlign: "center",
                            }}
                          >
                            {day.videos}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              cursor: "default",
                              width: "80px",
                              textAlign: "center",
                            }}
                          >
                            {day.notes}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              cursor: "default",
                              width: "90px",
                              textAlign: "center",
                            }}
                          >
                            {day.notebooks ?? 0}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              cursor: "default",
                              width: "80px",
                              textAlign: "center",
                            }}
                          >
                            {day.mcq}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              cursor: "default",
                              width: "100px",
                              textAlign: "center",
                            }}
                          >
                            {day.coding}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CourseOverview;
